import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * The app's one in-page loading indicator: a faint ring with a rounded arc
 * that grows and shrinks as it turns. The colour is currentColor (a button's
 * text, or a text-* class); the size is a button's icon size unless sized
 * here. The first-load splash (the Uplodio mark) lives in index.html.
 *
 * @example
 * import { Spinner } from "@/components/ui/spinner"
 *
 * export function Loading() {
 *   return (
 *     <div className="flex h-40 items-center justify-center text-muted-foreground">
 *       <Spinner className="size-5" />
 *     </div>
 *   )
 * }
 * // In a button it takes the button's text colour and icon size:
 * // <Button disabled={saving}>{saving && <Spinner />}Save</Button>
 */
function Spinner({ className, ...props }: React.ComponentProps<"svg">) {
  return (
    <svg
      role="status"
      aria-label="Loading"
      viewBox="0 0 24 24"
      width="24"
      height="24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      className={cn("shrink-0 animate-spin animation-duration-[1.4s]", className)}
      {...props}
    >
      <circle cx="12" cy="12" r="9.5" opacity={0.2} />
      {/* pathLength 100: the dash numbers are percentages of the ring. */}
      <circle cx="12" cy="12" r="9.5" pathLength={100} strokeDasharray="1 100">
        <animate
          attributeName="stroke-dasharray"
          values="1 100;60 100;60 100"
          keyTimes="0;0.5;1"
          calcMode="spline"
          keySplines="0.4 0 0.2 1;0.4 0 0.2 1"
          dur="1.4s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="stroke-dashoffset"
          values="0;-20;-99"
          keyTimes="0;0.5;1"
          calcMode="spline"
          keySplines="0.4 0 0.2 1;0.4 0 0.2 1"
          dur="1.4s"
          repeatCount="indefinite"
        />
      </circle>
    </svg>
  );
}

export { Spinner };
